import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { getLanguage, getSelectedExhibitionId } from '@app/app.reducer';
import { VerifyPhoneModalState } from '@components/verify-phone-modal/data-access/models/verify-phone-modal.model';
import { VerifyPhoneApi } from '@core/data-access/services/api/verify-phone-api';
import { ModalStateManager } from '@core/data-access/services/modal-state-manager';
import { Store } from '@ngrx/store';
import { SetIsPhoneVerified } from '@store/user/user.actions';
import { isEmpty } from 'lodash';
import { combineLatest, distinctUntilChanged, filter, first, of, switchMap, tap, withLatestFrom } from 'rxjs';

@Injectable()
export class VerifyPhoneModalFacade {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #verifyPhoneApi = inject(VerifyPhoneApi);
  readonly #modalStateManager = inject(ModalStateManager);

  readonly #verifyCodeFormControl = new FormControl<string>('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(6),
    Validators.pattern(/[^0-9]*$/)
  ]);

  readonly #state = this.setState();

  init() {
    const initRemoveErrorMessagesSubscription = this.initRemoveErrorMessagesSubscription();
    const initVerifyPhoneCheckSubscription = this.initVerifyPhoneCheckSubscription();

    return {
      initRemoveErrorMessagesSubscription: initRemoveErrorMessagesSubscription,
      initVerifyPhoneCheckSubscription: initVerifyPhoneCheckSubscription,
      state: this.#state
    };
  }

  setIsVerifyPhoneModalOpen(
    isVerifyPhoneModalOpen: boolean,
    isVerifyPhoneProcessing: boolean = false,
    isVerifyPhoneError: boolean = false,
    isVerifyPhoneResendProcessing: boolean = false,
    isVerifyPhoneResendError: boolean = false
  ) {
    this.#state.update((state) => ({
      ...state,
      viewModel: {
        isVerifyPhoneModalOpen,
        isVerifyPhoneProcessing,
        isVerifyPhoneError,
        isVerifyPhoneResendProcessing,
        isVerifyPhoneResendError
      }
    }));
  }

  verifyPhone() {
    return of(this.#verifyCodeFormControl.value)
      .pipe(
        tap(() => {
          this.setIsVerifyPhoneProcessing(true);

          this.#verifyCodeFormControl.reset('', { emitEvent: false });
        }),
        switchMap((verificationCode) => this.#verifyPhoneApi.postVerifyPhone$(verificationCode))
      )
      .subscribe({
        next: () => {
          this.#store.dispatch(new SetIsPhoneVerified(true));

          this.setIsVerifyPhoneModalOpen(false);
        },
        error: () => {
          this.#store.dispatch(new SetIsPhoneVerified(false));

          this.setIsVerifyPhoneProcessing(false, true, false);
        }
      });
  }

  verifyPhoneResend() {
    return combineLatest({
      language: this.#store.select(getLanguage),
      eventId: this.#store.select(getSelectedExhibitionId)
    })
      .pipe(
        first(({ language }) => !isEmpty(language)),
        tap(() => this.setIsVerifyPhoneResendProcessing(true)),
        switchMap(({ language, eventId }) => this.#verifyPhoneApi.postVerifyPhoneResend$(language, eventId))
      )
      .subscribe({
        next: () => this.setIsVerifyPhoneResendProcessing(false),
        error: () => this.setIsVerifyPhoneResendProcessing(false, false, true)
      });
  }

  private initRemoveErrorMessagesSubscription() {
    return this.#verifyCodeFormControl.valueChanges
      .pipe(
        distinctUntilChanged((_, currentValue) => isNaN(parseInt(currentValue))),
        withLatestFrom(toObservable(this.#state)),
        filter(
          ([
            _,
            {
              viewModel: { isVerifyPhoneError, isVerifyPhoneResendError }
            }
          ]) => isVerifyPhoneError || isVerifyPhoneResendError
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() =>
        this.#state.update((state) => ({
          ...state,
          viewModel: {
            ...state.viewModel,
            isVerifyPhoneError: false,
            isVerifyPhoneResendError: false
          }
        }))
      );
  }

  private initVerifyPhoneCheckSubscription() {
    return this.#modalStateManager.verifyPhoneModalState$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ isVerifyPhoneModalOpen }) => this.setIsVerifyPhoneModalOpen(isVerifyPhoneModalOpen));
  }

  private setIsVerifyPhoneProcessing(
    isVerifyPhoneProcessing: boolean,
    isVerifyPhoneError: boolean = false,
    isVerifyPhoneResendError: boolean = false
  ) {
    this.#state.update((state) => ({
      ...state,
      viewModel: {
        ...state.viewModel,
        isVerifyPhoneProcessing,
        isVerifyPhoneError,
        isVerifyPhoneResendError
      }
    }));
  }

  private setIsVerifyPhoneResendProcessing(
    isVerifyPhoneResendProcessing: boolean,
    isVerifyPhoneError: boolean = false,
    isVerifyPhoneResendError: boolean = false
  ) {
    this.#state.update((state) => ({
      ...state,
      viewModel: {
        ...state.viewModel,
        isVerifyPhoneResendProcessing,
        isVerifyPhoneError,
        isVerifyPhoneResendError
      }
    }));
  }

  private setState() {
    return signal<VerifyPhoneModalState>({
      viewModel: {
        isVerifyPhoneModalOpen: false,
        isVerifyPhoneProcessing: false,
        isVerifyPhoneError: false,
        isVerifyPhoneResendProcessing: false,
        isVerifyPhoneResendError: false
      },
      verifyCodeFormControl: this.#verifyCodeFormControl
    });
  }
}
